import { Fragment, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  
  Clock,
  Coffee,
  Crown,
  Church,
  Utensils,
  Sailboat,
  Ship,
  ShoppingBag,
  Footprints,
  Waves,
  Camera,
  Croissant,
  TreePine,
  ArrowRight,
  TrainFront,
  Info,
  MapPin,
  Plane,
  Stamp,
  Ticket,
} from "lucide-react";
import { Mark } from "@/components/Mark";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTivoliCard, GygTourBand, TIVOLI_TICKET_URL } from "@/components/GetYourGuide";
import { spotImg } from "@/data/planner";

const SITE = "https://cphhighlights.com";
const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/6ebajbre_Nyhavn.webp";

// very light **bold** parser (no links needed on this page)
const mdBold = (text) =>
  typeof text !== "string"
    ? text
    : text.split(/\*\*(.+?)\*\*/g).map((p, i) => (i % 2 === 1 ? <strong key={i} className="font-800 text-black">{p}</strong> : p));

// The day as a single Google Maps walking route — no airport legs (Maps can't
// mix the train ride with a many-stop walking route), so it starts where you
// step off the Metro at Christianshavn and ends at the central station, where
// you catch the train back to the airport. Place names so it resolves.
const cph = (s) => encodeURIComponent(`${s}, Copenhagen`);
const ROUTE = [
  "Christianshavn St.",
  "Bodenhoffs Bageri",
  "Amagergade",
  "Freetown Christiania",
  "Broens Gadekøkken",
  "Nyhavnsbroen",
  "Nettobådene, Nyhavn",
  "Amalienborg",
  "Marmorkirken",
  "Amagertorv",
  "BUKA Bakery, Frederiksberggade 21",
  "Tivoli Gardens",
  "Københavns Hovedbanegård",
];
const MAPS_ROUTE = `https://www.google.com/maps/dir/${ROUTE.map(cph).join("/")}/?travelmode=walking`;

// Load this exact day into the planner. A single-day (~1~) link keeps the
// author's stop order precisely and skips the planner's auto re-clustering;
// Broens (lunch) and Tivoli (dinner) already suppress the auto meal breaks, so
// this packed one-off loads as its own exception to the usual day rules.
const EDT_PLAN_URL =
  "/planner?trip=any~1~amagergade,christiania,broens,nyhavn,canaltour,amalienborg,marmorkirken,stroget,buka,tivoli&edt=1";

const STOPS = [
  {
    time: "08:45",
    icon: Plane,
    name: "Land & Metro straight to Christianshavn",
    body: (
      <>
        Step out of arrivals and follow the signs to the{" "}
        <strong className="font-800 text-black">Metro in Terminal 3</strong>.{" "}
        <strong className="font-800 text-black">M2 runs straight to Christianshavn in about 13 minutes</strong> — a{" "}
        <Link
          to="/transport"
          data-testid="edt-citypass-link"
          className="underline decoration-[#DF3226]/50 underline-offset-4 hover:text-[#DF3226] transition-colors"
        >
          City Pass or single ticket
        </Link>{" "}
        covers it. Christianshavn is one stop before Kongens Nytorv, so you start right in the old
        canal quarter and never double back — no extra metro all day.
      </>
    ),
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/okwy3pxy_2026-10-02%2015_22_51.008%2B0200.jpg",
    imgAlt: "The arrivals hall at Copenhagen Airport Terminal 3",
  },
  {
    time: "09:15",
    icon: Coffee,
    name: "Breakfast & coffee in Christianshavn",
    body:
      "First things first: fuel up right by the station on Christianshavns Torv. **Emmerys**, **Espresso House** or **Bodenhoffs Bageri** are all a few steps from the metro and right on the way to Amagergade. Grab a coffee and a bun to walk with.",
    img: spotImg("andersen", 1000),
    imgAlt: "Pastries and coffee at a Copenhagen bakery",
  },
  {
    time: "09:45",
    icon: Camera,
    name: "Amagergade — straight off the metro",
    body:
      "Your first stop is a two-minute stroll from the station: the cobbled lane of **Amagergade**, a row of 18th-century merchants' houses often called the prettiest street in Copenhagen (peek into the leafy Van Dockum courtyard halfway down). Pure old Christianshavn.",
    img: spotImg("amagergade", 1000),
    imgAlt: "The old cobbled street of Amagergade in Christianshavn, Copenhagen",
  },
  {
    time: "10:15",
    icon: Footprints,
    name: "Freetown Christiania",
    body:
      "A short walk inland brings you to **Freetown Christiania**, the car-free, self-governing community on an old military site — murals, workshops, lakeside paths and a one-of-a-kind atmosphere.",
    img: spotImg("christiania", 1000),
    imgAlt: "Colourful murals and greenery in Freetown Christiania",
  },
  {
    time: "11:30",
    icon: Waves,
    name: "Lunch at Broens Gadekøkken",
    body:
      "Head back to the waterfront at **Broens Gadekøkken**, a harbour-front street-food market right by Inderhavnsbroen. This is lunch: a couple of dozen stalls, from smørrebrød and burgers to tacos, coffee and cocktails — grab a seat looking across to Nyhavn.",
    img: spotImg("broens", 1000),
    imgAlt: "Harbour-front seating at Broens Gadekøkken looking across to Nyhavn",
  },
  {
    time: "12:15",
    icon: Sailboat,
    startHere: true,
    name: "Cross to Nyhavn — the postcard harbour",
    body: (
      <>
        Cross <strong className="font-800 text-black">Inderhavnsbroen</strong> (the 'kissing bridge') straight to <strong className="font-800 text-black">Nyhavn</strong>. The most photographed spot in the city.
        <br />
        <br />
        <strong className="font-800 text-[#DF3226]">Or start here.</strong> If you have less time in the city, this is where you'll start, with the metro from the airport to Kongens Nytorv. That's 4,5 km, 1 hour walk, 7 hours in the city.
      </>
    ),
    img: spotImg("nyhavn", 1000),
    imgAlt: "The colourful townhouses of Nyhavn reflected in the canal",
  },
  {
    time: "12:45",
    icon: Ship,
    name: "Canal boat tour",
    ticket: "https://gyg.me/d4it0SF2",
    body:
      "Jump on a **canal cruise straight from Nyhavn** (about an hour). It's the single best way to see the most ground in the least time — the Opera House, the Black Diamond and that new-angle skyline. Rest your legs while the city floats past.",
    img: spotImg("canaltour", 1000),
    imgAlt: "A canal boat tour passing colourful Copenhagen waterfront",
  },
  {
    time: "14:00",
    icon: Crown,
    name: "Amalienborg Palace square",
    body:
      "A flat ten-minute walk north from Nyhavn is **Amalienborg** — four rococo palaces around an octagonal courtyard, still the royal family's home, with the bearskin-hatted guards on their posts. The full **changing of the guard is at 12:00** daily, if your timing lines up.",
    img: spotImg("amalienborg", 1000),
    imgAlt: "Amalienborg Palace square in Copenhagen",
  },
  {
    time: "14:15",
    icon: Church,
    name: "Marmorkirken (The Marble Church)",
    body:
      "Right by the palace, step inside the **Marble Church** (free) under the largest church dome in Scandinavia and tip your head all the way back. A two-minute stop that punches well above its weight.",
    img: spotImg("marmorkirken", 1000),
    imgAlt: "The green dome of Frederik's Church (Marmorkirken)",
  },
  {
    time: "14:45",
    icon: ShoppingBag,
    name: "Amagertorv & up Strøget",
    body:
      "Drop down to **Amagertorv** — the handsome old square with the Stork Fountain — then turn onto **Strøget**, one of Europe's longest pedestrian streets. Browse your way west past the flagship **LEGO Store**, Illums Bolighus and the big-name shops, all the way toward Rådhuspladsen.",
    img: spotImg("stroget", 1000),
    imgAlt: "Shoppers strolling the Strøget pedestrian street in Copenhagen",
  },
  {
    time: "16:00",
    icon: Croissant,
    name: "Buka",
    body:
      "Right on **Strøget** sits **Buka** — a minimalist bakery known for its filled croissants (the pistachio is the one) and excellent cardamom buns. The perfect sugar hit for the last stretch, and it stays open until **19:00** — late enough to catch even after a delayed landing (the city's oldest bakery, Sankt Peders, shuts at 17:00).",
    img: spotImg("buka", 1000),
    imgAlt: "Filled croissants and cardamom buns on a Copenhagen bakery counter",
  },
  {
    time: "16:30",
    icon: Mark,
    name: "Tivoli Gardens by night",
    ticket: TIVOLI_TICKET_URL,
    body:
      "Strøget spills out at Rådhuspladsen, right beside **Tivoli Gardens** — the 1843 pleasure gardens that inspired Walt Disney. Have dinner inside (the Food Hall or a sit-down spot) and stay as the thousands of lights flicker on: flower beds, the century-old wooden rollercoaster and the whole garden glowing.",
    img: spotImg("tivoli", 1000),
    imgAlt: "Tivoli Gardens illuminated at dusk, Copenhagen",
    info: [
      ["Closed 4 January – 18 March 2027.", "Tivoli shuts between its Christmas and summer seasons — check the dates before planning a winter day trip."],
      ["Avoid Friday nights in summer.", "Entry jumps from 180 kr. to 275 kr. after 18:00 and the summer concerts pack it out — come another evening if you can."],
    ],
  },
  {
    id: "train",
    badge: "HEADING HOME",
    icon: TrainFront,
    name: "Train back to the airport",
    body:
      "When it's time to go, the **Øresundstog** train runs from the **central station (København H)**, a two-minute walk from Tivoli, straight back to the airport in just **17 minutes** — the perfect last act before your flight home.",
    img: "https://images.unsplash.com/photo-1649527266452-af2d5f52c5ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    imgAlt: "The arched wooden roof of Copenhagen Central Station (København H)",
  },
];

const ITEMLIST_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Copenhagen in one day — an extreme day trip itinerary",
  itemListElement: STOPS.map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.name })),
};

function StopCard({ stop, index }) {
  const reverse = index % 2 === 1;
  const testId = `edt-stop-${stop.id || stop.time.replace(":", "")}`;
  return (
    <Reveal y={40}>
      <div
        data-testid={testId}
        className="grid md:grid-cols-2 gap-6 md:gap-12 items-center py-8 md:py-12 border-b border-zinc-200/70 last:border-0"
      >
        <div className={`order-2 ${reverse ? "md:order-2" : "md:order-1"}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-[#DF3226] text-white font-display font-900 text-sm px-3 py-1 rounded-full">
              {stop.time ? (<><Clock size={14} /> {stop.time}</>) : stop.badge}
            </span>
            <span className="w-9 h-9 rounded-xl bg-black text-white flex items-center justify-center shrink-0">
              <stop.icon size={18} />
            </span>
          </div>
          <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-4xl">
            {stop.ticket ? (
              <a
                href={stop.ticket}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid={`${testId}-ticket`}
                className="underline decoration-[#DF3226]/50 underline-offset-8 hover: transition-colors"
              >
                {stop.name}
              </a>
            ) : (
              stop.name
            )}
          </h2>
          <p className="font-body text-base md:text-lg text-black/70 mt-4 leading-relaxed whitespace-pre-line">{mdBold(stop.body)}</p>
          {stop.info && (
            <div
              data-testid={`${testId}-info`}
              className="mt-6 bg-[#FCEFC5] border border-black shadow-brut rounded-xl p-5 flex items-start gap-3"
            >
              <Info size={18} className="shrink-0 mt-0.5 text-[#B8860B]" />
              <div className="space-y-2.5">
                {stop.info.map(([lead, rest], i) => (
                  <p key={i} className="font-body text-xs text-black/75 leading-relaxed">
                    <strong className="font-800 text-black">{lead}</strong> {rest}
                  </p>
                ))}
              </div>
            </div>
          )}
          {stop.ticket && (
            <a
              href={stop.ticket}
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid={`${testId}-book`}
              className="inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] mt-5 hover:gap-2.5 transition-all"
            >
              <Ticket size={15} /> Check times & book
            </a>
          )}
        </div>
        <div className={`order-1 ${reverse ? "md:order-1" : "md:order-2"}`}>
          <div className="border border-zinc-200 shadow-brut overflow-hidden aspect-[4/3]">
            <img
              src={stop.img}
              alt={stop.imgAlt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
            />
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function EdtPage() {
  const [ready, setReady] = useState("09:30");
  const READY_OPTIONS = ["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00"];
  return (
    <main data-testid="edt-page" className="bg-[#F9F8F6] text-black pt-16 md:pt-20">
      <Seo
        title="Extreme Day Trip to Copenhagen - EDT to Copenhagen - EDT Itinerary"
        description="The ultimate extreme day trip to Copenhagen — a packed first-timer's itinerary straight from the airport Metro: the royal palaces, Nyhavn, a canal cruise, Christiania and Tivoli by night, in one day."
        path="/edt"
        image={HERO_IMG}
        preloadImage={HERO_IMG}
      />
      <PageSchema
        path="/edt"
        name="Extreme Day Trip to Copenhagen — One Packed Day"
        description="A packed first-timer's Copenhagen itinerary straight from the airport Metro: the royal palaces, Nyhavn, a canal cruise, Christiania and Tivoli by night."
        crumb="Extreme day trip"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ITEMLIST_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Link
            to="/copenhagen-itinerary"
            data-testid="edt-back-link"
            className="inline-flex items-center gap-2 font-body font-700 text-sm text-white/70 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={16} /> The full 3-day itinerary
          </Link>
          <div className="mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.25em] text-white">EXTREME DAY TRIP IDEA</p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            EDT to Copenhagen<br />
            <span className="font-editorial italic font-600">Off the plane to Tivoli by night</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/75 mt-6 max-w-2xl leading-relaxed">
            Flying in just for the day? This is the packed, first-timer's route — off the airport Metro
            straight into Copenhagen's greatest hits: old Christianshavn, Freetown
            Christiania, a harbour lunch, Nyhavn, a canal cruise, the royal palace and
            Tivoli by night — all central, in the right order, with the timings worked. One day, nothing missed.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a
              href={MAPS_ROUTE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="edt-maps-route"
              className="inline-flex items-center gap-2 bg-[#DF3226] text-white font-body font-800 text-sm px-5 py-3 rounded-full hover:bg-white transition-colors"
            >
              <MapPin size={16} /> Open the whole route in Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* walking distance summary */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-10 md:pt-14">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 border-y border-dotted border-zinc-300 py-5">
          <div className="flex items-center gap-3">
            <Footprints size={24} className="text-[#DF3226] shrink-0" />
            <p className="font-body text-sm md:text-base text-black/75 leading-snug">
              <strong className="font-800 text-black">About 5,5 km on foot</strong> across the day
              (not counting the steps in Tivoli etc.) — roughly <strong className="font-800 text-black">1 h 15 min</strong> of walking.
            </p>
          </div>
        </div>
      </section>

      {/* honest maths + passport note */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-8 md:pt-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Reveal y={30}>
            <div data-testid="edt-honest-maths" className="h-full bg-white border border-zinc-200 shadow-brut rounded-xl p-6">
              <Clock size={22} className="text-[#DF3226]" />
              <h2 className="font-display font-900 tracking-tight text-xl mt-3">The honest maths</h2>
              <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">
                The plan below runs about 10 hours in the city, (&ldquo;short&rdquo; version 7 hours) walking included. Add the Metro 15 min and train 17 min + 10 min total wait. Be at security 2 hours before a flight. Treat it as inspiration, shuffle as you like on our planner.
              </p>
            </div>
          </Reveal>
          <Reveal y={30} delay={0.05}>
            <div data-testid="edt-passport" className="h-full bg-white border border-zinc-200 shadow-brut rounded-xl p-6">
              <Stamp size={22} className="text-[#DF3226]" />
              <h2 className="font-display font-900 tracking-tight text-xl mt-3">Non-EU passport? Add time</h2>
              <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">
                Flying in from outside the EU/Schengen? You'll clear passport control on the way out and again on the way back in — on busy days that's 30–60 minutes extra each way.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* seasonal Christmas cross-link */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-6 md:pt-8">
        <Reveal y={30}>
          <Link
            to="/christmas-walk"
            data-testid="edt-christmas-card"
            className="group flex items-center gap-4 md:gap-6 bg-[#0C3B2E] text-white rounded-xl p-5 md:p-6 border border-white/10 shadow-brut hover:-translate-y-0.5 transition-transform"
          >
            <div className="shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#DF3226] flex items-center justify-center">
              <TreePine size={26} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body font-800 text-[11px] tracking-[0.25em] text-[#DF3226]">FLYING IN AROUND CHRISTMAS?</p>
              <h2 className="font-display font-900 tracking-tight text-lg md:text-xl leading-tight mt-1">
                Swap this for our festive Christmas walk
              </h2>
            </div>
            <ArrowRight size={22} className="shrink-0 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all" />
          </Link>
        </Reveal>
      </section>

      {/* timeline */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-4 md:pt-6 pb-4">
        {STOPS.map((stop, i) => (
          <Fragment key={stop.id || stop.time}>
            {stop.startHere && (
              <Reveal y={30}>
                <div data-testid="edt-start-here" className="pt-4 md:pt-6 pb-2">
                  <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-4xl md:text-5xl">
                    Start from here
                  </h2>
                  <h3 className="font-editorial italic font-600 text-xl md:text-2xl text-black/70 mt-3 max-w-2xl">
                    If you're not that extreme
                  </h3>
                </div>
              </Reveal>
            )}
            <StopCard stop={stop} index={i} />
          </Fragment>
        ))}
      </section>

      {/* tivoli monetisation card */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-1 pb-8">
        <GygTivoliCard />
      </section>

      {/* load into planner */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pb-4">
        <div className="bg-[#13141A] text-white rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex-1">
            <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl leading-tight">
              Make it yours
            </h2>
            <p className="font-body text-sm md:text-base text-white/70 mt-2 leading-relaxed max-w-xl">
              Load the day into the planner — every stop in order, ready to drop a spot, add
              your own, see it on a map and save or share it. Don't mind the warnings or counting,
              the planner just thinks it's a wild day 😉
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <label htmlFor="edt-ready-select" className="font-body font-800 text-xs tracking-[0.18em] text-white/60">
                READY IN THE CITY AT
              </label>
              <select
                id="edt-ready-select"
                data-testid="edt-ready-select"
                value={ready}
                onChange={(e) => setReady(e.target.value)}
                className="font-display font-800 text-sm bg-white text-[#13141A] rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#DF3226]"
              >
                {READY_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              {ready !== "09:30" && (
                <span className="font-body text-xs text-white/50">the whole day shifts to match</span>
              )}
            </div>
            <p className="font-body text-sm md:text-base text-white/70 mt-3 leading-relaxed max-w-xl" data-testid="edt-ready-note">
              If you're in the city later than 11.30 remove <strong className="font-800 text-white/70">Buka</strong> (will be closed later). If you have less hours in the city or want maximum time in Tivoli then skip <strong className="font-800 text-white/70">Amagergade</strong>, <strong className="font-800 text-white/70">Christiania</strong> and <strong className="font-800 text-white/70">Broens Gadekøkken</strong>, then take the metro from the airport to Kongens Nytorv (right next to Nyhavn) instead.
            </p>
          </div>
          <Link
            to={`${EDT_PLAN_URL}&ready=${ready}`}
            data-testid="edt-load-planner"
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#DF3226] text-white font-display font-800 text-base px-7 py-4 rounded-full hover:bg-white transition-colors"
          >
            Load this day into the planner
          </Link>
        </div>
      </section>

      {/* Book a ticket — Tivoli Gardens (shared big-ad design) */}
      <GygTourBand tourId="72048" label="BOOK A TICKET" />

      <ExploreLinks
        testid="explore-edt"
        links={[
          { label: "Got a few hours only? The layover guide", to: "/layover" },
          { label: "The relaxed version — 3 perfect days", to: "/copenhagen-itinerary" },
          { label: "Build your own day — the free planner", to: "/planner" },
        ]}
      />

      <ShareBar title="Extreme Day Trip to Copenhagen — One Packed Day" />
    </main>
  );
}
